import { createContext, useContext, useState, ReactNode } from 'react';

// Nombres de ciudades griegas para las mesas
const greekCities = [
  'Atenas', 'Santorini', 'Mykonos', 'Creta', 'Rodas', 'Corfú', 
  'Delfos', 'Olimpia', 'Meteora', 'Salónica', 'Náfplio', 'Paros',
  'Naxos', 'Zakynthos', 'Kefalonia', 'Hydra', 'Ítaca', 'Delos',
  'Samos', 'Esparta', 'Corinto', 'Tebas'
];

export interface Table {
  id: number;
  city: string;
  seats: number;
  status: 'available' | 'occupied' | 'reserved';
  occupiedSince?: string;
  orderId?: string;
  reservationId?: string;
  reservationTime?: string;
  reservationMinutes?: number;
  customerName?: string;
  branch: string;
  futureReservation?: { // Nueva propiedad para reservas futuras sin ocupar la mesa
    reservationId: string;
    reservationTime: string;
    reservationMinutes: number;
    customerName: string;
    customerPhone?: string;
  };
}

interface TablesContextType {
  tables: Table[];
  occupyTable: (tableId: number, orderId: string) => void;
  reserveTable: (tableId: number, reservationId: string, reservationTime: string, customerName: string, reservationMinutes?: number) => void;
  freeTable: (tableId: number) => void;
  addFutureReservation: (tableId: number, reservationId: string, reservationTime: string, customerName: string, customerPhone: string, reservationMinutes: number) => void;
  getAvailableTables: (minSeats?: number, branch?: string) => Table[];
  getTableById: (tableId: number) => Table | undefined;
  getTablesByBranch: (branch: string) => Table[];
}

const TablesContext = createContext<TablesContextType | undefined>(undefined);

const initialTables: Table[] = [
  // Chapinero - 16 mesas
  { id: 1, city: 'Atenas', seats: 2, status: 'available', branch: 'Bogotá - Chapinero', futureReservation: { reservationId: 'RES-101', reservationTime: '18:30', reservationMinutes: 45, customerName: 'Ana García', customerPhone: '310-555-1234' } },
  { id: 2, city: 'Santorini', seats: 2, status: 'reserved', reservationId: 'CH-RES-001', reservationTime: '19:00', reservationMinutes: 340, customerName: 'Carlos Martínez', branch: 'Bogotá - Chapinero' },
  { id: 3, city: 'Mykonos', seats: 4, status: 'occupied', occupiedSince: '12:30 PM', orderId: '#CH-001', branch: 'Bogotá - Chapinero' },
  { id: 4, city: 'Creta', seats: 4, status: 'available', branch: 'Bogotá - Chapinero' },
  { id: 5, city: 'Rodas', seats: 4, status: 'occupied', occupiedSince: '12:30 PM', orderId: '#CH-001', branch: 'Bogotá - Chapinero' },
  { id: 6, city: 'Corfú', seats: 6, status: 'available', branch: 'Bogotá - Chapinero' },
  { id: 7, city: 'Delfos', seats: 2, status: 'available', branch: 'Bogotá - Chapinero' },
  { id: 8, city: 'Olimpia', seats: 4, status: 'reserved', reservationId: 'CH-RES-001', reservationTime: '19:00', reservationMinutes: 340, customerName: 'Carlos Martínez', branch: 'Bogotá - Chapinero' },
  { id: 9, city: 'Meteora', seats: 4, status: 'available', branch: 'Bogotá - Chapinero' },
  { id: 10, city: 'Salónica', seats: 6, status: 'available', branch: 'Bogotá - Chapinero', futureReservation: { reservationId: 'RES-102', reservationTime: '19:15', reservationMinutes: 55, customerName: 'Luis Rojas', customerPhone: '315-555-5678' } },
  { id: 11, city: 'Náfplio', seats: 2, status: 'available', branch: 'Bogotá - Chapinero' },
  { id: 12, city: 'Paros', seats: 4, status: 'occupied', occupiedSince: '12:40 PM', orderId: '#CH-003', branch: 'Bogotá - Chapinero' },
  { id: 13, city: 'Naxos', seats: 6, status: 'available', branch: 'Bogotá - Chapinero' },
  { id: 14, city: 'Zakynthos', seats: 8, status: 'available', branch: 'Bogotá - Chapinero' },
  { id: 15, city: 'Kefalonia', seats: 6, status: 'reserved', reservationId: 'CH-RES-003', reservationTime: '21:00', reservationMinutes: 460, customerName: 'Juan Pérez', branch: 'Bogotá - Chapinero' },
  { id: 16, city: 'Hydra', seats: 4, status: 'available', branch: 'Bogotá - Chapinero' },

  // Usaquén - 14 mesas
  { id: 17, city: 'Atenas', seats: 4, status: 'occupied', occupiedSince: '13:00 PM', orderId: '#US-001', branch: 'Bogotá - Usaquén' },
  { id: 18, city: 'Ítaca', seats: 2, status: 'available', branch: 'Bogotá - Usaquén' },
  { id: 19, city: 'Delos', seats: 6, status: 'available', branch: 'Bogotá - Usaquén' },
  { id: 20, city: 'Samos', seats: 4, status: 'available', branch: 'Bogotá - Usaquén' },
  { id: 21, city: 'Esparta', seats: 8, status: 'occupied', occupiedSince: '13:10 PM', orderId: '#US-004', branch: 'Bogotá - Usaquén' },
  { id: 22, city: 'Corinto', seats: 2, status: 'reserved', reservationId: 'US-RES-001', reservationTime: '18:30', reservationMinutes: 270, customerName: 'Diana Rodríguez', branch: 'Bogotá - Usaquén' },
  { id: 23, city: 'Tebas', seats: 4, status: 'available', branch: 'Bogotá - Usaquén' },
  { id: 24, city: 'Santorini', seats: 6, status: 'available', branch: 'Bogotá - Usaquén' },
  { id: 25, city: 'Mykonos', seats: 4, status: 'available', branch: 'Bogotá - Usaquén' },
  { id: 26, city: 'Creta', seats: 2, status: 'available', branch: 'Bogotá - Usaquén' },
  { id: 27, city: 'Rodas', seats: 10, status: 'reserved', reservationId: 'US-RES-002', reservationTime: '19:30', reservationMinutes: 330, customerName: 'Pedro Sánchez', branch: 'Bogotá - Usaquén' },
  { id: 28, city: 'Olimpia', seats: 4, status: 'occupied', occupiedSince: '12:50 PM', orderId: '#US-003', branch: 'Bogotá - Usaquén' },
  { id: 29, city: 'Delfos', seats: 6, status: 'available', branch: 'Bogotá - Usaquén' },
  { id: 30, city: 'Meteora', seats: 4, status: 'available', branch: 'Bogotá - Usaquén' },

  // Zona T - 12 mesas
  { id: 31, city: 'Atenas', seats: 2, status: 'available', branch: 'Bogotá - Zona T' },
  { id: 32, city: 'Santorini', seats: 4, status: 'available', branch: 'Bogotá - Zona T' },
  { id: 33, city: 'Mykonos', seats: 6, status: 'occupied', occupiedSince: '14:00 PM', orderId: '#ZT-001', branch: 'Bogotá - Zona T' },
  { id: 34, city: 'Creta', seats: 4, status: 'reserved', reservationId: 'ZT-RES-001', reservationTime: '21:00', reservationMinutes: 360, customerName: 'Andrés Jiménez', branch: 'Bogotá - Zona T' },
  { id: 35, city: 'Rodas', seats: 8, status: 'available', branch: 'Bogotá - Zona T' },
  { id: 36, city: 'Corfú', seats: 2, status: 'available', branch: 'Bogotá - Zona T' },
  { id: 37, city: 'Delfos', seats: 4, status: 'available', branch: 'Bogotá - Zona T' },
  { id: 38, city: 'Olimpia', seats: 6, status: 'occupied', occupiedSince: '13:55 PM', orderId: '#ZT-003', branch: 'Bogotá - Zona T' },
  { id: 39, city: 'Meteora', seats: 4, status: 'available', branch: 'Bogotá - Zona T' },
  { id: 40, city: 'Salónica', seats: 12, status: 'reserved', reservationId: 'ZT-RES-002', reservationTime: '19:00', reservationMinutes: 240, customerName: 'Carolina Ruiz', branch: 'Bogotá - Zona T' },
  { id: 41, city: 'Náfplio', seats: 2, status: 'available', branch: 'Bogotá - Zona T' },
  { id: 42, city: 'Paros', seats: 4, status: 'available', branch: 'Bogotá - Zona T' },
];

export function TablesProvider({ children }: { children: ReactNode }) {
  const [tables, setTables] = useState<Table[]>(initialTables);

  const occupyTable = (tableId: number, orderId: string) => {
    setTables(prev => prev.map(table => {
      if (table.id === tableId) {
        return {
          ...table,
          status: 'occupied' as const,
          orderId,
          occupiedSince: new Date().toLocaleTimeString('es-CO', { timeZone: 'America/Bogota', 
            hour: '2-digit',
            minute: '2-digit'
          }),
          // Limpiar datos de reserva si existían
          reservationId: undefined,
          reservationTime: undefined,
          reservationMinutes: undefined,
        };
      }
      return table;
    }));
  };

  const reserveTable = (tableId: number, reservationId: string, reservationTime: string, customerName: string, reservationMinutes?: number) => {
    setTables(prev => prev.map(table => {
      if (table.id === tableId) {
        return {
          ...table,
          status: 'reserved' as const,
          reservationId,
          reservationTime,
          reservationMinutes,
          customerName,
        };
      }
      return table;
    }));
  };

  const freeTable = (tableId: number) => {
    setTables(prev => prev.map(table => {
      if (table.id === tableId) {
        return {
          ...table,
          status: 'available' as const,
          orderId: undefined,
          occupiedSince: undefined,
          reservationId: undefined,
          reservationTime: undefined,
          reservationMinutes: undefined,
          customerName: undefined,
          futureReservation: undefined, // También limpiar reservas futuras
        };
      }
      return table;
    }));
  };

  const addFutureReservation = (tableId: number, reservationId: string, reservationTime: string, customerName: string, customerPhone: string, reservationMinutes: number) => {
    setTables(prev => prev.map(table => {
      if (table.id === tableId) {
        return {
          ...table,
          futureReservation: {
            reservationId,
            reservationTime,
            reservationMinutes,
            customerName,
            customerPhone,
          },
        };
      }
      return table;
    }));
  };

  const getTablesByBranch = (branch: string) => {
    return tables.filter(table => table.branch === branch);
  };

  const getAvailableTables = (minSeats?: number, branch?: string) => {
    return tables.filter(table => {
      const isAvailable = table.status === 'available';
      const hasEnoughSeats = minSeats ? table.seats >= minSeats : true;
      const matchesBranch = branch ? table.branch === branch : true;
      return isAvailable && hasEnoughSeats && matchesBranch;
    });
  };

  const getTableById = (tableId: number) => {
    return tables.find(table => table.id === tableId);
  };

  return (
    <TablesContext.Provider value={{
      tables,
      occupyTable,
      reserveTable,
      freeTable,
      addFutureReservation,
      getAvailableTables,
      getTableById,
      getTablesByBranch,
    }}>
      {children}
    </TablesContext.Provider>
  );
}

export function useTables() {
  const context = useContext(TablesContext);
  if (context === undefined) {
    throw new Error('useTables must be used within a TablesProvider');
  }
  return context;
}